import { create } from 'zustand'
import { api } from '@/services/api'
import type { ContentItem, ReferenceLink } from '@/types'
import { LibraryMapper } from '@/services/mappers/libraryMapper'
import type { CompanyInfo, AnalysisResult } from '@/types'

// Global lock to prevent concurrent/duplicate fetchCompanyInfo calls
let companyInfoFetchPromise: Promise<void> | null = null

interface Prompt {
  id: string
  text: string
  loading: boolean
  results: any[] | null
  category: 'blog' | 'reddit'
  created_at?: string
}

interface AppStore {
  prompts: Prompt[]
  contentLibrary: ContentItem[]
  referenceLinks: ReferenceLink[]
  selectedPromptIds: string[]
  selectedPlatforms: string[]
  generatedContents: Record<string, any>
  isGenerating: boolean
  viewingLibraryItem: ContentItem | null
  promptCount: number
  libraryCount: number
  loadingCitations: boolean
  companyInfo: CompanyInfo | null
  isAnalyzing: boolean

  // Actions
  setPrompts: (items: Prompt[]) => void
  fetchPrompts: (params?: { limit?: number; offset?: number }) => Promise<void>
  createPrompt: (text: string, category?: 'blog' | 'reddit') => Promise<Prompt | null>
  deletePrompt: (id: string) => Promise<void>
  addPrompt: (text: string, category?: 'blog' | 'reddit') => Prompt
  updatePromptResults: (id: string, results: any) => void
  togglePromptSelection: (id: string) => void
  clearPromptSelection: () => void
  togglePlatform: (platform: string) => void
  setSelectedPlatforms: (platforms: string[]) => void
  addContent: (content: Omit<ContentItem, 'id'>) => ContentItem
  updateContent: (id: string, updates: Partial<ContentItem>) => void
  setContentLibrary: (items: ContentItem[]) => void
  fetchLibrary: (params?: { status?: 'draft' | 'published' | 'scheduled'; limit?: number; offset?: number }) => Promise<void>
  upsertLibraryItem: (item: ContentItem) => void
  addReferenceLink: (link: Omit<ReferenceLink, 'id'> & { id?: string }) => void
  removeReferenceLink: (id: string) => void
  removeReferenceLinkFromAPI: (id: string) => Promise<void>
  updateReferenceLinks: (links: ReferenceLink[]) => void
  loadCitationsFromPrompts: (promptIds: string[]) => Promise<void>
  setGenerating: (value: boolean) => void
  setGeneratedContents: (contents: Record<string, any>) => void
  setViewingLibraryItem: (item: ContentItem | null) => void
  resetStudio: () => void
  setCompanyInfo: (info: CompanyInfo | null) => void
  updateCompanyInfo: (updates: Partial<CompanyInfo>) => void
  setAnalysisResult: (result: AnalysisResult) => void
  setIsAnalyzing: (value: boolean) => void
  clearCompanyInfo: () => void

  // Company actions
  analyzeCompany: (url: string) => Promise<void>
  reanalyzeCompany: () => Promise<void>
  fetchCompanyInfo: () => Promise<void>
  updateCompanyInfoFromAPI: (updates: { name?: string; industry?: string; description?: string }) => Promise<void>
}

export const useAppStore = create<AppStore>((set, get) => ({
  prompts: [],
  contentLibrary: [],
  referenceLinks: [],
  selectedPromptIds: [],
  selectedPlatforms: ['blog'],
  generatedContents: {},
  isGenerating: false,
  viewingLibraryItem: null,
  promptCount: 0,
  libraryCount: 0,
  loadingCitations: false,

  setPrompts: (items) => {
    set({ prompts: items, promptCount: items.length })
  },

  fetchPrompts: async (params) => {
    try {
      const response = await api.prompts.getAll(params)
      if (response.success) {
        // Backend now returns prompts with results included
        const normalized: Prompt[] = (response.data || []).map((p: any) => ({
          id: String(p.id),
          text: String(p.text ?? ''),
          loading: Boolean(p.loading),
          // Map backend results to frontend format (platform_id -> platformId, citation_count -> citationCount)
          results: Array.isArray(p.results)
            ? p.results.map((r: any) => ({
                platformId: r.platform_id,
                mentioned: r.mentioned,
                sentiment: r.sentiment,
                citationCount: r.citation_count ?? 0,
                brandCitationCount: r.brand_citation_count ?? 0,
                status: r.status,
              }))
            : null,
          category: (p.category as 'blog' | 'reddit') ?? 'blog',
          created_at: p.created_at,
        }))
        get().setPrompts(normalized)
      }
    } catch (error) {
      console.error('Failed to fetch prompts:', error)
    }
  },

  createPrompt: async (text, category = 'blog') => {
    try {
      const response = await api.prompts.create({ text, category })
      if (response.success && response.data) {
        const created: Prompt = {
          id: String((response.data as any).id),
          text: String((response.data as any).text ?? ''),
          loading: Boolean((response.data as any).loading),
          results: null,
          category: ((response.data as any).category as 'blog' | 'reddit') ?? category,
        }
        set((state) => ({
          ...state,
          prompts: [created, ...state.prompts],
          promptCount: state.promptCount + 1,
        }))
        return created
      }
    } catch (error) {
      console.error('Failed to create prompt:', error)
    }
    return null
  },

  deletePrompt: async (id) => {
    try {
      await api.prompts.delete(id)
      set((state) => {
        const nextPrompts = state.prompts.filter((p) => p.id !== id)
        const nextSelected = state.selectedPromptIds.filter((pid) => pid !== id)
        return {
          ...state,
          prompts: nextPrompts,
          promptCount: nextPrompts.length,
          selectedPromptIds: nextSelected,
        }
      })
    } catch (error) {
      console.error('Failed to delete prompt:', error)
    }
  },
  companyInfo: null,
  isAnalyzing: false,

  addPrompt: (text, category = 'blog') => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const newPrompt: Prompt = {
      id: tempId,
      text,
      loading: true,
      results: null,
      category
    }
    set((state) => ({
      ...state,
      prompts: [newPrompt, ...state.prompts],
      promptCount: state.promptCount + 1
    }))
    return newPrompt
  },

  updatePromptResults: (id, results) => {
    set((state) => ({
      ...state,
      prompts: state.prompts.map(p =>
        p.id === id ? { ...p, loading: false, results } : p
      )
    }))
  },

  togglePromptSelection: (id) => {
    const selectedPromptIds = [...get().selectedPromptIds]
    const index = selectedPromptIds.indexOf(id)
    if (index > -1) {
      selectedPromptIds.splice(index, 1)
    } else {
      selectedPromptIds.push(id)
    }
    set({ selectedPromptIds })
  },

  clearPromptSelection: () => {
    set({ selectedPromptIds: [] })
  },

  togglePlatform: (platform) => {
    const selectedPlatforms = [...get().selectedPlatforms]
    const index = selectedPlatforms.indexOf(platform)
    if (index > -1) {
      selectedPlatforms.splice(index, 1)
    } else {
      selectedPlatforms.push(platform)
    }
    set({ selectedPlatforms })
  },

  setSelectedPlatforms: (platforms) => {
    const normalized = (platforms || []).filter((p) => typeof p === 'string' && p.trim())
    set({ selectedPlatforms: normalized.length > 0 ? normalized : ['blog'] })
  },

  addContent: (content) => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const newItem: ContentItem = {
      ...content,
      id: tempId
    }
    set((state) => ({
      ...state,
      contentLibrary: [newItem, ...state.contentLibrary],
      libraryCount: state.libraryCount + 1
    }))
    return newItem
  },

  updateContent: (id, updates) => {
    set((state) => ({
      ...state,
      contentLibrary: state.contentLibrary.map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    }))
  },

  setContentLibrary: (items) => {
    set((state) => ({
      ...state,
      contentLibrary: items,
      libraryCount: items.length,
    }))
  },

  fetchLibrary: async (params) => {
    try {
      const response = await api.library.getAll(params)
      if (response.success) {
        get().setContentLibrary(LibraryMapper.toContentItems(response.data || []))
      }
    } catch (error) {
      console.error('Failed to fetch library:', error)
    }
  },

  upsertLibraryItem: (item) => {
    set((state) => {
      const index = state.contentLibrary.findIndex((it) => it.id === item.id)
      const next = [...state.contentLibrary]
      if (index > -1) {
        next[index] = { ...next[index], ...item }
      } else {
        next.unshift(item)
      }
      return {
        ...state,
        contentLibrary: next,
        libraryCount: next.length,
      }
    })
  },

  addReferenceLink: (link) => {
    const { id: providedId, ...rest } = link
    const newLink: ReferenceLink = {
      ...rest,
      id: providedId || `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    }
    set((state) => ({
      ...state,
      referenceLinks: [...state.referenceLinks, newLink]
    }))
  },

  removeReferenceLink: (id) => {
    set((state) => ({
      ...state,
      referenceLinks: state.referenceLinks.filter(link => link.id !== id)
    }))
  },

  // Remove reference link via API
  removeReferenceLinkFromAPI: async (id) => {
    const link = get().referenceLinks.find(l => l.id === id)
    // Only call API for manual links
    if (link?.source === 'manual') {
      try {
        await api.citations.delete(id)
      } catch (error) {
        console.error('Failed to delete citation:', error)
        throw error
      }
    }
    // Update local state
    get().removeReferenceLink(id)
  },

  updateReferenceLinks: (links) => {
    set({ referenceLinks: links })
  },

  // Load citations from prompts via API
  loadCitationsFromPrompts: async (promptIds) => {
    if (promptIds.length === 0) {
      const manualLinks = get().referenceLinks.filter(l => l.source === 'manual')
      set({ referenceLinks: manualLinks })
      return
    }

    set({ loadingCitations: true })

    try {
      // Keep manual links
      const manualLinks = get().referenceLinks.filter(l => l.source === 'manual')
      const autoLinks: ReferenceLink[] = []

      // Fetch citations for each prompt
      for (const promptId of promptIds) {
        try {
          const response = await api.citations.getByPrompt(promptId)
          if (response.success && response.data) {
            const citations = response.data.map(c => ({
              id: c.id,
              url: c.url,
              title: c.title,
              domain: c.domain,
              source: c.reference_source,
              citationSource: c.citation_source,
              promptId: c.prompt_id,
              snippet: c.snippet,
              autoImported: c.auto_imported,
            }))
            autoLinks.push(...citations)
          }
        } catch (error) {
          console.error(`Failed to load citations for prompt ${promptId}:`, error)
        }
      }

      set({ referenceLinks: [...manualLinks, ...autoLinks] })
    } finally {
      set({ loadingCitations: false })
    }
  },

  setGenerating: (value) => {
    set({ isGenerating: value })
  },

  setGeneratedContents: (contents) => {
    set({ generatedContents: contents })
  },

  setViewingLibraryItem: (item) => {
    set({ viewingLibraryItem: item })
  },

  resetStudio: () => {
    set({
      selectedPromptIds: [],
      selectedPlatforms: ['blog'],
      generatedContents: {},
      referenceLinks: [],
      viewingLibraryItem: null
    })
  },

  setCompanyInfo: (info) => {
    set({ companyInfo: info })
  },

  updateCompanyInfo: (updates) => {
    const currentInfo = get().companyInfo
    if (currentInfo) {
      set({ companyInfo: { ...currentInfo, ...updates } })
    }
  },

  setAnalysisResult: (result) => {
    const currentInfo = get().companyInfo
    if (currentInfo) {
      set({ companyInfo: { ...currentInfo, analysisResult: result } })
    }
  },

  setIsAnalyzing: (value) => {
    set({ isAnalyzing: value })
  },

  clearCompanyInfo: () => {
    set({ companyInfo: null })
  },

  // ==================== Company Actions ====================

  analyzeCompany: async (url: string) => {
    const { setIsAnalyzing } = get()
    setIsAnalyzing(true)

    try {
      const response = await api.company.analyze({ url })
      if (response.success) {
        // /company/analyze returns company fields only (no analysisResult).
        // Refresh full company + analysisResult from /company/my_info to avoid blank results UI.
        await get().fetchCompanyInfo()
      } else if (response.data) {
        set({ companyInfo: response.data })
      }
    } catch (error) {
      console.error('Analysis failed:', error)
    } finally {
      setIsAnalyzing(false)
    }
  },

  reanalyzeCompany: async () => {
    const { companyInfo, setIsAnalyzing } = get()
    if (!companyInfo?.url) return

    setIsAnalyzing(true)

    try {
      const response = await api.company.analyze({ url: companyInfo.url })
      if (response.success) {
        await get().fetchCompanyInfo()
      } else if (response.data) {
        set({ companyInfo: response.data })
      }
    } catch (error) {
      console.error('Re-analysis failed:', error)
    } finally {
      setIsAnalyzing(false)
    }
  },

  fetchCompanyInfo: async () => {
    // If already fetching, return existing promise to prevent duplicate calls
    if (companyInfoFetchPromise) {
      return companyInfoFetchPromise
    }

    companyInfoFetchPromise = (async () => {
      try {
        const response = await api.company.getMyInfo()
        if (response.success) {
          if (response.data?.company) {
            set({
              companyInfo: {
                ...response.data.company,
                analysisResult: response.data.analysisResult || undefined
              }
            })
          } else {
            set({ companyInfo: null })
          }
        }
      } catch (error) {
        console.error('Failed to fetch company info:', error)
      } finally {
        companyInfoFetchPromise = null
      }
    })()

    return companyInfoFetchPromise
  },

  updateCompanyInfoFromAPI: async (updates: { name?: string; industry?: string; description?: string }) => {
    try {
      const response = await api.company.update(updates)
      if (response.success && response.data) {
        const currentInfo = get().companyInfo
        if (currentInfo) {
          set({ companyInfo: { ...currentInfo, ...response.data } })
        }
      }
    } catch (error) {
      console.error('Failed to update company info:', error)
    }
  },
}))
